import React, { useState, useEffect } from "react";
import {
  FileText,
  AlertCircle,
  Filter,
  Building,
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Calendar,
  Receipt,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";
import PaymentRecordingModal from "./components/PaymentRecordingModal";
import CreateReceivableModal from "./components/CreateReceivableModal";

const SUBSIDIARIES = [
  { value: "", label: "All Subsidiaries" },
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Partially Received", label: "Partially Received" },
  { value: "Fully Received", label: "Fully Received" },
];

const AGING_OPTIONS = [
  { value: "", label: "All Ages" },
  { value: "30", label: "0-30 days" },
  { value: "60", label: "31-60 days" },
  { value: "90", label: "61-90 days" },
  { value: "91", label: "90+ days" },
];

const ReceivablesManagement = () => {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total: 0,
    received: 0,
    outstanding: 0,
  });
  const [filters, setFilters] = useState({
    status: "",
    subsidiary: "",
    aging: "",
    receivable: "",
  });
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchReceivables();
  }, [filters]);

  const fetchReceivables = async () => {
    try {
      setLoading(true);
      const response = await financeService.getReceivables(filters);
      
      console.log('📊 Receivables API Response:', response.data);
      
      if (response.data.success) {
        const receivablesData = response.data.data.receivables || [];
        console.log('📋 Receivables Data:', receivablesData);
        
        // Fetch payment history for each receivable
        const receivablesWithPayments = await Promise.all(
          receivablesData.map(async (receivable) => {
            try {
              const paymentsResponse = await financeService.getReceivablePaymentHistory(receivable.id);
              const payments = paymentsResponse.data.success ? paymentsResponse.data.data.payments || [] : [];
              console.log(`💰 Receivable ${receivable.invoice_number} has ${payments.length} payments:`, payments);
              return { ...receivable, Payments: payments };
            } catch (error) {
              console.warn(`⚠️ Failed to fetch payments for ${receivable.invoice_number}:`, error);
              return { ...receivable, Payments: [] };
            }
          })
        );
        
        setReceivables(receivablesWithPayments);
        
        // Calculate summary
        const total = receivablesWithPayments.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
        
        // Total Received = Sum of all payments added to receivables
        const received = receivablesWithPayments.reduce((sum, r) => {
          if (r.Payments && Array.isArray(r.Payments)) {
            const paymentsTotal = r.Payments.reduce((paySum, payment) => paySum + parseFloat(payment.amount || 0), 0);
            console.log(`💵 Receivable ${r.invoice_number} has ${r.Payments.length} payments totaling:`, paymentsTotal);
            return sum + paymentsTotal;
          }
          return sum;
        }, 0);
        
        console.log('📊 Summary - Total:', total, 'Received:', received, 'Outstanding:', total - received);
        
        // Total Outstanding = Total Receivables - Total Received
        const outstanding = total - received;
        
        setSummary({ total, received, outstanding });
      }
    } catch (error) {
      console.error("Error fetching receivables:", error);
      toast.error("Failed to load receivables");
    } finally {
      setLoading(false);
    }
  };

  const getAgingColor = (days) => {
    if (days <= 30) return "text-green-600 bg-green-50";
    if (days <= 60) return "text-yellow-600 bg-yellow-50";
    if (days <= 90) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Pending": { color: "bg-orange-100 text-orange-800", icon: Clock },
      "Partially Received": { color: "bg-blue-100 text-blue-800", icon: AlertCircle },
      "Fully Received": { color: "bg-green-100 text-green-800", icon: CheckCircle },
    };
    
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", icon: XCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const handleRecordPayment = (receivable) => {
    setSelectedReceivable(receivable);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedReceivable(null);
    fetchReceivables();
    toast.success("Payment received recorded successfully");
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchReceivables();
    toast.success("Receivable created successfully");
  };

  const toggleRowExpansion = (receivableId) => {
    setExpandedRow(expandedRow === receivableId ? null : receivableId);
  };

  const formatCurrency = (amount) => {
    return financeService.formatCurrency(amount || 0);
  };

  // Filter receivables based on selected filters
  const filteredReceivables = receivables.filter((receivable) => {
    // Filter by specific receivable
    if (filters.receivable && receivable.id.toString() !== filters.receivable) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading receivables...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Accounts Receivable
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            <span>Manage client invoices and payments</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              {receivables.length} Active
            </span>
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Receivable
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Receivables</p>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {formatCurrency(summary.total)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  All outstanding invoices
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <FileText className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-400 to-green-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Received</p>
                </div>
                <p className="text-3xl font-bold text-green-600 mb-1">
                  {formatCurrency(summary.received)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Payments collected
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-400 to-red-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Outstanding</p>
                </div>
                <p className="text-3xl font-bold text-red-600 mb-1">
                  {formatCurrency(summary.outstanding)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Amount pending
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <AlertCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Filter className="h-4 w-4 text-blue-600" />
            </div>
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Select Receivable
              </label>
              <Select
                value={filters.receivable}
                onValueChange={(value) => setFilters({ ...filters, receivable: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="All Receivables" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Receivables</SelectItem>
                  {receivables.map((receivable) => (
                    <SelectItem key={receivable.id} value={receivable.id.toString()}>
                      {receivable.client_name} - {receivable.invoice_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Status
              </label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Subsidiary
              </label>
              <Select
                value={filters.subsidiary}
                onValueChange={(value) => setFilters({ ...filters, subsidiary: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="All Subsidiaries" />
                </SelectTrigger>
                <SelectContent>
                  {SUBSIDIARIES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Aging
              </label>
              <Select
                value={filters.aging}
                onValueChange={(value) => setFilters({ ...filters, aging: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  {AGING_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receivables List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 via-white to-blue-50 border-b border-blue-100">
          <CardTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <span className="text-xl font-bold text-gray-900">Receivables List</span>
              <p className="text-xs text-gray-500 mt-0.5">Click to expand and view payment details</p>
            </div>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-3 py-1">
              {filteredReceivables.length} items
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredReceivables.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No receivables found</p>
              <p className="text-sm text-gray-400 mt-1">
                {receivables.length === 0 
                  ? "Create a receivable to track client payments"
                  : "Try adjusting your filters"
                }
              </p>
              {receivables.length === 0 && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Receivable
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredReceivables.map((receivable) => {
                const receivedFromPayments = receivable.Payments && Array.isArray(receivable.Payments)
                  ? receivable.Payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0)
                  : 0;
                
                const balance = parseFloat(receivable.amount || 0) - receivedFromPayments;
                const aging = receivable.aging_days || 0;
                const isExpanded = expandedRow === receivable.id;
                
                return (
                  <div key={receivable.id} className={`transition-all duration-200 ${isExpanded ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
                    {/* Main Row */}
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left Section - Client & Invoice */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleRowExpansion(receivable.id)}
                              className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 ${
                                isExpanded 
                                  ? 'bg-blue-600 text-white shadow-md' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <h3 className="font-bold text-gray-900 truncate text-lg">
                                  {receivable.client_name}
                                </h3>
                                {getStatusBadge(receivable.payment_status)}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs font-semibold">
                                  {receivable.invoice_number}
                                </span>
                                {receivable.Project && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <div className="flex items-center gap-1.5">
                                      <FolderOpen className="h-3.5 w-3.5 text-blue-500" />
                                      <span className="truncate font-medium">{receivable.Project.name}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Middle Section - Amounts */}
                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                            <p className="font-bold text-gray-900 text-base">{formatCurrency(receivable.amount)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Received</p>
                            <p className="font-bold text-green-600 text-base">{formatCurrency(receivedFromPayments)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Balance</p>
                            <p className="font-bold text-red-600 text-base">{formatCurrency(balance)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Aging</p>
                            <Badge className={`${getAgingColor(aging)} font-bold`}>
                              {aging}d
                            </Badge>
                          </div>
                        </div>

                        {/* Right Section - Actions */}
                        <div className="flex items-center gap-2">
                          {receivable.payment_status !== "Fully Received" && (
                            <Button
                              size="sm"
                              onClick={() => handleRecordPayment(receivable)}
                              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all duration-200 font-semibold"
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Add Payment
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-5 pb-5 bg-gradient-to-b from-blue-50/50 to-white border-t border-blue-100">
                        <div className="grid grid-cols-12 gap-6 pt-5">
                          {/* Left Column - Info */}
                          <div className="col-span-4 space-y-4">
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                                <Building className="h-3.5 w-3.5" />
                                Subsidiary
                              </label>
                              <p className="text-sm font-semibold text-gray-900">
                                {SUBSIDIARIES.find(s => s.value === receivable.subsidiary)?.label || receivable.subsidiary}
                              </p>
                            </div>
                            
                            {receivable.description && (
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">
                                  Description
                                </label>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {receivable.description}
                                </p>
                              </div>
                            )}

                            <div className="grid grid-cols-1 gap-3">
                              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Total Amount</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(receivable.amount)}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Total Received</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(receivedFromPayments)}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Balance Due</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(balance)}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Payment History */}
                          <div className="col-span-8">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                              <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Receipt className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <h4 className="font-bold text-gray-900">Payment History</h4>
                                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                    {receivable.Payments?.length || 0} payments
                                  </Badge>
                                </div>
                              </div>

                              <div className="p-4">
                                {!receivable.Payments || receivable.Payments.length === 0 ? (
                                  <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                      <Receipt className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-600">No payments recorded yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Payments will appear here once recorded</p>
                                  </div>
                                ) : (
                                  <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                                    {receivable.Payments.map((payment, index) => (
                                      <div
                                        key={payment.id || index}
                                        className="bg-gradient-to-r from-green-50 to-white border border-green-100 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                                              <CheckCircle className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                              <p className="font-bold text-gray-900 text-lg">
                                                {formatCurrency(payment.amount)}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1">
                                                <Calendar className="h-3 w-3 text-gray-400" />
                                                <p className="text-xs text-gray-600 font-medium">
                                                  {financeService.formatDate(payment.payment_date || payment.createdAt)}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            {payment.payment_method && (
                                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-1.5 text-xs font-semibold">
                                                {payment.payment_method}
                                              </Badge>
                                            )}
                                            {payment.reference_number && (
                                              <p className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                                                {payment.reference_number}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        {payment.notes && (
                                          <div className="mt-3 pt-3 border-t border-green-100">
                                            <p className="text-xs text-gray-600 italic">{payment.notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Recording Modal */}
      {showPaymentModal && selectedReceivable && (
        <PaymentRecordingModal
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          payable={selectedReceivable}
          onSuccess={handlePaymentSuccess}
          type="receivable"
        />
      )}

      {/* Create Receivable Modal */}
      <CreateReceivableModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default ReceivablesManagement;
